import React from 'react';
import { AutoSizer } from 'react-virtualized';
import styled from 'styled-components';
import {
  IReportState,
  ReportSettings,
} from '../../../data/providers/reports/types';
import { ISettingsTemplate } from '../../../data/providers/settings/reports/types';
import { Entity } from '../../../data/types';
import { OverrideDarkMode } from '../../../Theme';
import { Button } from '@ui/shared/Button';
import { SelectBox } from '../form/SelectBox';
import { Icon } from '../Icon';
import { LinearLoading } from '../loading/LinearLoading';
import html2pdf from './html2pdf';
import { PdfBase } from './PdfBase';
import { PdfHeader } from './PdfHeader';

interface IProps {
  onComplete: () => void;
  onStart?: () => void;
  title: string;
  secondaryText?: string;
  children: any;
  filename: string;
  entity: Entity;
  loading?: boolean;
  reportState?: IReportState;
  onPdfModeChange: (pdfMode: EPdfMode) => void;
  pdfMode: EPdfMode;
}

export enum EPdfMode {
  DEFAULT,
  PREVIEW,
  PRINT,
}

export const Pdf: React.FunctionComponent<IProps> = (
  props: IProps
): React.ReactElement => {
  const [generating, setGenerating] = React.useState<boolean>(false);

  /**
   * @description sets generating true
   */
  const generate = (): void => {
    setGenerating(true);
  };
  /**
   * @description generates the pdf when `generating` is flipped to true
   */
  React.useEffect((): void => {
    if (generating) {
      /// delay generation until all the elements have been redrawn, this is especially important to make sure
      /// any graphs have the opportunity to resize themselves
      setTimeout(() => {
        requestAnimationFrame(() => {
          const bgColor =
            props.pdfMode === EPdfMode.PRINT
              ? '#fff'
              : getComputedStyle(document.documentElement).getPropertyValue(
                  '--color-surface-0'
                );
          html2pdf(null, {
            html2canvas: {
              scrollX: 0,
              scrollY: 0,
              windowWidth: document.documentElement.offsetWidth,
              windowHeight: document.documentElement.offsetHeight,
              backgroundColor: bgColor,
            },
          })
            .from(document.getElementById('pdf-export'))
            .set({
              pagebreak: { mode: ['css', 'legacy'], avoid: '.avoid-break' },
              backgroundColor: bgColor,
              image: { type: 'jpeg', quality: 0.98 },
              html2canvas: {
                scale: 1.2,
                backgroundColor: bgColor,
                dpi: 192,
                logging: false,
              },
              jsPDF: { unit: 'mm', format: 'a4' },
            })
            .toPdf()
            .get('pdf')
            .save(props.filename)
            .catch((err) => console.error(err))
            .then(() => {
              /// add delay to allow file picker to open
              setTimeout((): void => {
                setGenerating(false);
              }, 1000);
            });
        });
      }, 2000);
    }
  }, [generating]);

  const printModeHandler = (e: React.ChangeEvent<HTMLInputElement>): void => {
    props.onPdfModeChange(
      e.currentTarget.checked ? EPdfMode.PRINT : EPdfMode.PREVIEW
    );
  };

  return (
    <React.Fragment>
      <div style={{ height: '100%', width: '100%' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50px',
            backgroundColor: 'var(--color-surface-1)',
          }}
        >
          <PdfActions>
            <div style={{ flex: 1 }} />
            <PdfActionCenter>
              <Button
                title='Download'
                onClick={generate}
                disabled={
                  props.loading || !props.reportState?.isValid || generating
                }
               />
            </PdfActionCenter>
            <PdfActionRight>
              <SelectBox
                label='Printable View'
                selected={props.pdfMode === EPdfMode.PRINT}
                onSelect={printModeHandler}
              />
            </PdfActionRight>
          </PdfActions>
        </div>
        {generating && <LinearLoading />}
        {props.loading ? (
          <LinearLoading />
        ) : (
          <PdfPreviewContainer generating={generating}>
            <AutoSizer>
              {({
                height,
                width,
              }: {
                height: number;
                width: number;
              }): React.ReactElement => {
                return (
                  <PdfBase
                    style={{ width, height }}
                    parentHeight={height - 20}
                    parentWidth={width}
                    generating={generating}
                  >
                    <OverrideDarkMode
                      override={props.pdfMode === EPdfMode.PRINT}
                      id='pdf-export'
                      style={{
                        backgroundColor: 'var(--color-surface-0)',
                        border: '2px solid var(--color-surface-0',
                      }}
                    >
                      <PdfHeader
                        title={props.title}
                        entity={props.entity}
                        timeframe={props.reportState?.timeframe}
                        templateName={
                          (
                            props.reportState
                              ?.settings as ISettingsTemplate<ReportSettings>
                          )?.name
                        }
                      />
                      {props.children}
                    </OverrideDarkMode>
                  </PdfBase>
                );
              }}
            </AutoSizer>
            {generating && <GeneratingMessage />}
          </PdfPreviewContainer>
        )}
      </div>
    </React.Fragment>
  );
};

const GeneratingMessage = (): React.ReactElement => {
  return (
    <GeneratingContainer>
      <GeneratingContent>
        <Icon
          size='large'
          name='hourglass_empty'
          style={{ textAlign: 'center' }}
        />
        <h6 style={{ textAlign: 'center' }}>Generating PDF</h6>
      </GeneratingContent>
    </GeneratingContainer>
  );
};

const GeneratingContainer = styled.div`
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  position: absolute;
  background-color: var(--color-surface-0);
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  align-items: center;
  justify-content: center;
`;

const GeneratingContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const PdfActions = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const PdfActionCenter = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`;

const PdfActionRight = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const PdfPreviewContainer = styled.div<{ generating: boolean }>`
  padding-top: 50px;
  height: 100%;
  width: 100%;
  position: relative;
`;

export const isPdf = (mode: EPdfMode): boolean => {
  return mode === EPdfMode.PREVIEW || mode === EPdfMode.PRINT;
};

export const shouldInvert = (mode: EPdfMode): boolean =>
  mode === EPdfMode.PRINT;
